import { useEffect, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL, getToken, USE_MOCK_API } from '../../shared/api/httpClient'
import { recognizeDocument, confirmRecognizedEvent, type OcrResult } from '../../shared/api/ocr'
import { Button } from '../../shared/components/Button'
import type { Document } from '../../shared/api/types'

export function OcrPanel({tripId,doc,onClose}:{tripId:string;doc:Document;onClose:()=>void}) {
  const {t}=useTranslation();const qc=useQueryClient()
  const [preview,setPreview]=useState<OcrResult|null>(null)
  const [original,setOriginal]=useState('')
  const [title,setTitle]=useState('');const [date,setDate]=useState('');const [time,setTime]=useState('')
  const [location,setLocation]=useState('');const [duration,setDuration]=useState(0)
  const [busy,setBusy]=useState(false);const [error,setError]=useState('')

  useEffect(()=>{let active=true;let url=''
    async function load(){
      try {
        if (USE_MOCK_API) {setOriginal(doc.file_url);return}
        const token=getToken();const response=await fetch(`${API_BASE_URL}/files/${doc.file_url}`,
          {headers:token?{Authorization:`Bearer ${token}`}:{}})
        if (!response.ok) throw new Error(t('ocr.original_error'))
        url=URL.createObjectURL(await response.blob());if(active)setOriginal(url)
      } catch(err){if(active)setError(err instanceof Error?err.message:String(err))}
    }
    void load();return()=>{active=false;if(url)URL.revokeObjectURL(url)}
  },[doc.file_url,t])

  async function recognize(){setBusy(true);setError('')
    try{const result=await recognizeDocument(tripId,doc.id);setPreview(result)
      setTitle(result.candidate.title ?? '');setDate(result.candidate.date ?? '')
      setTime(result.candidate.time ?? '');setLocation(result.candidate.location ?? '')}
    catch(err){setError(err instanceof Error?err.message:String(err))}finally{setBusy(false)} }
  async function confirm(e:FormEvent){e.preventDefault();setBusy(true);setError('')
    try{await confirmRecognizedEvent(tripId,doc.id,{title:title.trim(),date:date||null,time:time||null,
      location,type:'event',duration_minutes:duration});await Promise.all([
        qc.invalidateQueries({queryKey:['documents',tripId]}),qc.invalidateQueries({queryKey:['points',tripId]})]);onClose()}
    catch(err){setError(err instanceof Error?err.message:String(err))}finally{setBusy(false)} }
  const isImage=/\.(png|jpe?g|webp|tiff?)$/i.test(doc.file_url)
  return <div role="dialog" aria-modal="true" aria-label={t('ocr.title')} className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
    <div className="mx-auto mt-4 max-w-5xl rounded-st-lg bg-st-surface p-4 shadow-xl">
      <div className="mb-3 flex items-center justify-between"><h2 className="text-xl font-semibold">{t('ocr.title')}</h2>
        <Button variant="ghost" onClick={onClose}>{t('ocr.close')}</Button></div>
      <div className="grid gap-4 md:grid-cols-2">
        <section><h3 className="mb-2 font-semibold">{t('ocr.original')}</h3>
          {original ? isImage ? <img src={original} alt={t('ocr.original')} className="max-h-[65vh] w-full object-contain" />
            : <iframe src={original} title={t('ocr.original')} className="h-[65vh] w-full" />
            : <p>{t('ocr.original_error')}</p>}</section>
        <section className="space-y-3"><Button variant="outline" disabled={busy} onClick={()=>void recognize()}>{busy?t('ocr.reading'):t('ocr.read')}</Button>
          {preview&&<><div className="rounded border border-st-line p-2 text-sm"><h3 className="font-semibold">{t('ocr.text')}</h3>
            <pre className="max-h-32 overflow-auto whitespace-pre-wrap">{preview.text || t('ocr.no_text')}</pre></div>
            {preview.warnings.map((w,i)=><p key={i} className="text-sm text-st-muted">{w}</p>)}
            <form onSubmit={e=>void confirm(e)} className="space-y-2">
              <label className="block text-sm">{t('ocr.event')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" required value={title} onChange={e=>setTitle(e.target.value)} /></label>
              <div className="grid grid-cols-2 gap-2"><label className="text-sm">{t('ocr.date')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" type="date" value={date} onChange={e=>setDate(e.target.value)} /></label>
                <label className="text-sm">{t('ocr.time')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" type="time" value={time} onChange={e=>setTime(e.target.value)} /></label></div>
              <label className="block text-sm">{t('ocr.location')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" value={location} onChange={e=>setLocation(e.target.value)} /></label>
              <label className="block text-sm">{t('ocr.duration')}<input className="ms-2 w-20 rounded border border-st-line bg-st-surface p-2" type="number" min="0" value={duration} onChange={e=>setDuration(Number(e.target.value))} /></label>
              <Button type="submit" variant="accent" disabled={busy}>{t('ocr.confirm')}</Button>
            </form></>}
          {error&&<p role="alert" className="text-sm text-st-danger">{error}</p>}
        </section>
      </div>
    </div>
  </div>
}
